import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { SlotRendering as SlotTypesSlotRendering } from '@frontend/slot/types';
import { SpotRenderingClient } from '@frontend/spot/api';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ChooseModuleModal } from '../../components/choose-module-modal/choose-module-modal.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import { SpotLayout } from '../../spot-rendering';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { OpenAllSlotsConfirmModal } from './open-all-slots-confirm-modal.component';
import { useOpenSlots } from './open-slots.controller';

const OpenSlots = () => {
    const navigate = useKioskNavigate();
    const viewProps = useOpenSlots();
    const [openAllConfirmOpen, setOpenAllConfirmOpen] = useState(false);
    const showMainContent = !viewProps.isLoading && !viewProps.error;

    const handleOpenSlot = () => {
        if (viewProps.selectedSlot) viewProps.openSlot(viewProps.selectedSlot);
    };

    const handleOpenAllConfirm = () => {
        if (viewProps.currentModule) viewProps.openAllSlotsInModule(viewProps.currentModule);
        setOpenAllConfirmOpen(false);
    };

    return (
        <div className='bg-background-default text-foreground-default flex size-full flex-col items-center justify-start overflow-hidden font-medium'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={() => navigate(-1)}
            />

            <div className='flex size-full flex-1 flex-col items-center justify-center overflow-hidden p-6'>
                {viewProps.isLoading && <Spinner />}
                {viewProps.error && <div className='text-status-danger'>{viewProps.error}</div>}
                {showMainContent && (
                    <div className='flex size-full w-full flex-1 flex-col items-start gap-6 overflow-y-auto pb-4'>
                        <div className='w-full space-y-4'>
                            <p className='text-foreground-default w-full text-lg font-semibold'>
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.utilities'
                                    defaultMessage='Utilities'
                                />
                                {': '}
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.open-slots'
                                    defaultMessage='Open SLOTs'
                                />
                            </p>
                            <div className='text-foreground-default flex w-full flex-wrap items-center justify-between gap-3 text-base font-medium'>
                                <p>
                                    {viewProps.selectedSlot ? (
                                        <FormattedMessage
                                            id='kiosk.manage-spot.selected-slot'
                                            defaultMessage='Selected {slot}'
                                            values={{
                                                slot: viewProps.selectedSlot.display ?? viewProps.selectedSlot.number ?? ''
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id='kiosk.manage-spot.select-slot'
                                            defaultMessage='Select a SLOT'
                                        />
                                    )}
                                </p>
                                {viewProps.currentModule && (
                                    <button
                                        type='button'
                                        onClick={() => setOpenAllConfirmOpen(true)}
                                        disabled={viewProps.isOpeningModuleSlots}
                                        className='text-brand-primary cursor-pointer border-0 bg-transparent hover:opacity-80 disabled:opacity-50'>
                                        <FormattedMessage
                                            id='kiosk.manage-spot.open-slots.open-all-button'
                                            defaultMessage='Open all'
                                            description='Button to open all slots in current module'
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className='flex w-full flex-col items-center gap-4'>
                            {viewProps.currentModule && viewProps.accountId && viewProps.spotId && (
                                <div className='flex size-full max-w-md flex-row justify-center'>
                                    <SpotLayout<ModuleRendering, SlotRendering>
                                        key={viewProps.currentModule.id}
                                        renderMode='module'
                                        module={viewProps.currentModule}
                                        fetchSpotData={(arg) => SpotRenderingClient.fetchSpotRendering(viewProps.accountId!, viewProps.spotId!, arg)}
                                        fetchModuleData={(accountId, spotId, moduleId) =>
                                            SpotRenderingClient.fetchSpotModuleRendering(accountId, spotId, moduleId)
                                        }
                                        slot={{
                                            renderElement: (slot) =>
                                                viewProps.slotComponent({
                                                    ...slot,
                                                    slot_ids: [slot.id],
                                                    column_slot_ids: null
                                                } as unknown as SlotTypesSlotRendering)
                                        }}
                                        enableSlotHover={false}
                                        onModuleNameClick={() => viewProps.setSelectModuleModalOpen(true)}
                                        onSelectedModuleChange={(moduleId) => {
                                            const module = viewProps.modules?.find((m) => m.id === moduleId);
                                            if (module) viewProps.setCurrentModule(module);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={() => navigate(-1)}
                    />
                }
                rightButton={
                    <DefaultPrimaryButton
                        text={
                            <FormattedMessage
                                id='kiosk.manage-spot.open-slots.open-slot-button'
                                defaultMessage='Open SLOT {name}'
                                values={{
                                    name: viewProps.selectedSlot?.display ?? viewProps.selectedSlot?.number ?? ''
                                }}
                            />
                        }
                        onClick={handleOpenSlot}
                        disabled={!showMainContent || !viewProps.selectedSlot || viewProps.isOpeningSlot}
                        isLoading={viewProps.isOpeningSlot}
                    />
                }
            />

            {showMainContent && (
                <>
                    <ChooseModuleModal
                        modules={viewProps.modules}
                        show={viewProps.selectModuleModalOpen}
                        onClose={() => viewProps.setSelectModuleModalOpen(false)}
                        onSelectModule={viewProps.setCurrentModule}
                        currentModule={viewProps.currentModule}
                    />
                    <OpenAllSlotsConfirmModal
                        show={openAllConfirmOpen}
                        onClose={() => setOpenAllConfirmOpen(false)}
                        onConfirm={handleOpenAllConfirm}
                        isOpening={viewProps.isOpeningModuleSlots}
                        moduleIndex={viewProps.currentModule?.index ?? ''}
                    />
                </>
            )}
        </div>
    );
};

export default OpenSlots;
